import { useEffect, useRef, useState } from "react";
import { mockNotificationsEnabled } from "@/hooks/notification-mocks";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Bell } from "lucide-react";
import { useNotifications } from "@/hooks/use-notifications";
import { useTranslations } from "@/hooks/use-translations";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/api";
import { NotificationItem } from "./notification-item";
export function NotificationCenter() {
	const [params] = useSearchParams(),
		navigate = useNavigate(),
		t = useTranslations("notifications");
	const [open, setOpen] = useState(params.has("notification")),
		[message, setMessage] = useState("");
	const query = useNotifications(open, params.get("notification"));
	const containerRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (!open) return;
		const handlePointerDown = (event: PointerEvent) => {
			if (
				event.target instanceof Node &&
				!containerRef.current?.contains(event.target)
			) {
				setOpen(false);
			}
		};
		document.addEventListener("pointerdown", handlePointerDown);
		return () => document.removeEventListener("pointerdown", handlePointerDown);
	}, [open]);
	return (
		<div ref={containerRef} className="relative">
			<Button
				variant="ghost"
				size="icon"
				aria-label={t("title")}
				aria-expanded={open}
				onClick={() => setOpen((v) => !v)}
				className="relative"
			>
				<Bell size={18} />
				{!!query.data?.unread && (
					<span className="absolute -top-1 -right-1 rounded-full bg-danger px-1 text-xs text-bg-surface">
						{query.data.unread}
					</span>
				)}
			</Button>
			{open && (
				<section
					aria-label={t("title")}
					style={{ right: -310, top:60 }}
					className="absolute top-10 w-[min(440px,90vw)] max-h-[75vh] overflow-y-auto bg-bg-surface border border-border-subtle rounded-xl shadow-popover z-50"
				>
					<div className="flex items-center justify-between p-4 border-b border-border-subtle text-text-primary">
						<strong>{t("title")}</strong>
						<Button
							variant="ghost"
							size="icon-xs"
							onClick={() => setOpen(false)}
							aria-label={t("close")}
						>
							×
						</Button>
					</div>
					{query.isPending && (
						<p className="p-4 text-text-secondary">{t("loading")}</p>
					)}
					{query.error && (
						<p role="alert" className="p-4 text-danger">
							{t("error")}
						</p>
					)}
					{query.focused.error && (
						<p role="alert" className="p-4 text-danger">
							{t("error")}
						</p>
					)}
					{message && (
						<p role="status" className="p-4 text-text-secondary">
							{message}
						</p>
					)}
					{!query.isPending && !query.error && !query.data?.items.length && (
						<p className="p-4 text-text-secondary">{t("empty")}</p>
					)}
					{query.data?.items.map((item) => (
						<NotificationItem
							key={item.id}
							item={item}
							pending={query.answer.isPending || query.recheck.isPending}
							onRead={() => query.read.mutate(item.id)}
							onAnswer={(action) =>
								query.answer.mutate(
									{ reviewId: item.review_id!, action },
									{
										onSuccess: (result) =>
											setMessage(
												t(
													result.status === "needs_clarification"
														? "clarify"
														: "saved",
												),
											),
										onError: () => setMessage(t("error")),
									},
								)
							}
							onRecheck={() =>
								query.recheck.mutate(item.payload.executionId!, {
									onSuccess: (r) =>
										setMessage(
											t(
												`unconfirmed.result.${r.result === "unreachable" && r.reason === "access_denied" ? "access_denied" : r.result}`,
											),
										),
									onError: (error) =>
										setMessage(
											t(
												error instanceof ApiError && error.code === "recheck_forbidden"
													? "unconfirmed.result.forbidden"
													: "error",
											),
										),
								})
							}
							onDiscuss={() => {
								if (mockNotificationsEnabled) {
									setMessage("Demo: mock notification");
									return;
								}
								setOpen(false);
								navigate(
									// A creative's buttons are on its card in the chat; there is no review to discuss.
									item.payload.kind === "creative"
										? `/workspace/${item.payload.conversationId}`
										: `/workspace/${item.payload.conversationId}?review=${item.review_id ?? ""}&notification=${item.id}`,
								);
							}}
						/>
					))}
					{query.hasNextPage && (
						<button
							className="p-4 text-text-link underline underline-offset-2 disabled:cursor-not-allowed disabled:text-action-disabled-fg"
							disabled={query.isFetchingNextPage}
							onClick={() => void query.fetchNextPage()}
						>
							{t("loadMore")}
						</button>
					)}
				</section>
			)}
		</div>
	);
}
